/**
 * Canvas-built textures for the artwork studio: poster cards with rounded corners,
 * a simplified Gilmours Instagram profile card, and a soft drop-shadow sprite.
 */
import * as THREE from 'three';

export const CARD_W = 280;
export const CARD_H = (280 * 16) / 9;
const TEX_W = 1080;
const TEX_H = Math.round((TEX_W * CARD_H) / CARD_W);
const RADIUS = (20 * TEX_W) / CARD_W;
const API = process.env.REACT_APP_BACKEND_URL;

export const proxied = (url) => `${API}/api/artwork/image?url=${encodeURIComponent(url)}`;

export const loadImage = (url) => new Promise((resolve, reject) => {
  const im = new Image();
  im.crossOrigin = 'anonymous';
  im.onload = () => resolve(im);
  im.onerror = () => reject(new Error(`image failed: ${url}`));
  im.src = proxied(url);
});

const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

const cover = (ctx, img, x, y, w, h) => {
  const s = Math.max(w / img.width, h / img.height);
  ctx.drawImage(img, x + (w - img.width * s) / 2, y + (h - img.height * s) / 2, img.width * s, img.height * s);
};

const toTexture = (canvas, renderer) => {
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = renderer.capabilities.getMaxAnisotropy();
  tex.generateMipmaps = true;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  return tex;
};

export async function posterTexture(url, renderer) {
  const img = await loadImage(url);
  const c = document.createElement('canvas');
  c.width = TEX_W; c.height = TEX_H;
  const ctx = c.getContext('2d');
  roundRect(ctx, 0, 0, TEX_W, TEX_H, RADIUS);
  ctx.clip();
  ctx.fillStyle = '#0d2213';
  ctx.fillRect(0, 0, TEX_W, TEX_H);
  cover(ctx, img, 0, 0, TEX_W, TEX_H);
  return toTexture(c, renderer);
}

// Simplified Gilmours profile (header · avatar + stats · bio · buttons · 3×3 grid)
export async function instagramTexture(images, avatarUrl, renderer) {
  const [avatar, ...tiles] = await Promise.all([loadImage(avatarUrl), ...images.slice(0, 9).map(loadImage)]);
  const c = document.createElement('canvas');
  c.width = TEX_W; c.height = TEX_H;
  const ctx = c.getContext('2d');
  const u = TEX_W / CARD_W; // css px → texture px
  roundRect(ctx, 0, 0, TEX_W, TEX_H, RADIUS);
  ctx.clip();
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, TEX_W, TEX_H);
  const font = (px, weight = 400) => { ctx.font = `${weight} ${px * u}px -apple-system, "Segoe UI", Helvetica, Arial, sans-serif`; };

  // header
  ctx.fillStyle = '#111'; font(15, 700); ctx.textBaseline = 'middle';
  ctx.fillText('gilmours', 14 * u, 22 * u);
  ctx.fillStyle = '#3897f0'; font(11, 700);
  ctx.fillText('✓', 86 * u, 22 * u);
  ctx.fillStyle = '#111'; font(16, 700);
  ctx.fillText('···', TEX_W - 40 * u, 21 * u);

  // avatar ring + stats
  const ax = 44 * u; const ay = 78 * u; const ar = 31 * u;
  const grad = ctx.createLinearGradient(ax - ar, ay + ar, ax + ar, ay - ar);
  grad.addColorStop(0, '#f9ce34'); grad.addColorStop(0.5, '#ee2a7b'); grad.addColorStop(1, '#6228d7');
  ctx.beginPath(); ctx.arc(ax, ay, ar + 3 * u, 0, Math.PI * 2); ctx.fillStyle = grad; ctx.fill();
  ctx.beginPath(); ctx.arc(ax, ay, ar + 1 * u, 0, Math.PI * 2); ctx.fillStyle = '#fff'; ctx.fill();
  ctx.save(); ctx.beginPath(); ctx.arc(ax, ay, ar, 0, Math.PI * 2); ctx.clip();
  cover(ctx, avatar, ax - ar, ay - ar, ar * 2, ar * 2); ctx.restore();
  const stats = [[`${images.length * 14}`, 'posts'], ['2,431', 'followers'], ['312', 'following']];
  stats.forEach(([n, l], i) => {
    const sx = (120 + i * 55) * u;
    ctx.textAlign = 'center';
    ctx.fillStyle = '#111'; font(15, 700); ctx.fillText(n, sx, 70 * u);
    ctx.fillStyle = '#333'; font(11, 400); ctx.fillText(l, sx, 88 * u);
  });
  ctx.textAlign = 'left';

  // bio
  ctx.fillStyle = '#111'; font(12.5, 700); ctx.fillText('Gilmours Estate Agents', 14 * u, 126 * u);
  ctx.fillStyle = '#333'; font(11.5, 400); ctx.fillText('Padstow · Sales · Lettings · Lifestyle', 14 * u, 143 * u);
  ctx.fillStyle = '#00376b'; font(11.5, 500); ctx.fillText('gilmours.co.uk', 14 * u, 160 * u);

  // buttons
  const by = 176 * u; const bh = 26 * u;
  roundRect(ctx, 14 * u, by, 120 * u, bh, 7 * u); ctx.fillStyle = '#0095f6'; ctx.fill();
  roundRect(ctx, 140 * u, by, 98 * u, bh, 7 * u); ctx.fillStyle = '#efefef'; ctx.fill();
  roundRect(ctx, 244 * u, by, 24 * u, bh, 7 * u); ctx.fill();
  ctx.textAlign = 'center'; font(12, 700);
  ctx.fillStyle = '#fff'; ctx.fillText('Follow', 74 * u, by + bh / 2);
  ctx.fillStyle = '#111'; ctx.fillText('Message', 189 * u, by + bh / 2);
  ctx.fillText('▾', 256 * u, by + bh / 2);
  ctx.textAlign = 'left';

  // tabs rule
  ctx.fillStyle = '#dbdbdb'; ctx.fillRect(0, 214 * u, TEX_W, 1 * u);
  ctx.fillStyle = '#111'; ctx.fillRect(0, 214 * u, TEX_W / 3, 1.5 * u);

  // grid
  const gap = 1.5 * u; const tile = (TEX_W - gap * 2) / 3; const gy = 217 * u;
  tiles.forEach((img, i) => {
    const col = i % 3; const row = Math.floor(i / 3);
    ctx.save();
    ctx.beginPath(); ctx.rect(col * (tile + gap), gy + row * (tile + gap), tile, tile); ctx.clip();
    cover(ctx, img, col * (tile + gap), gy + row * (tile + gap), tile, tile);
    ctx.restore();
  });
  return toTexture(c, renderer);
}

export const SHADOW_PAD = 90;
export function shadowTexture(renderer) {
  const w = CARD_W + SHADOW_PAD * 2; const h = CARD_H + SHADOW_PAD * 2;
  const c = document.createElement('canvas');
  c.width = Math.round(w); c.height = Math.round(h);
  const ctx = c.getContext('2d');
  ctx.filter = 'blur(26px)';
  ctx.fillStyle = 'rgba(0,0,0,1)';
  roundRect(ctx, SHADOW_PAD, SHADOW_PAD, CARD_W, CARD_H, 20);
  ctx.fill();
  const tex = new THREE.CanvasTexture(c);
  tex.anisotropy = renderer.capabilities.getMaxAnisotropy();
  return tex;
}
